<template>
  <section class="rag-page" :dir="pageDirection">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>
        <h3>{{ tr("loadingTitle") }}</h3>
        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>
      <p>{{ tr("pageDescription") }}</p>
      <span>{{ tr("reference") }}</span>
    </div>

    <div class="legend">
      <div><span class="dot green"></span> {{ tr("legend.green") }}</div>
      <div><span class="dot amber"></span> {{ tr("legend.amber") }}</div>
      <div><span class="dot red"></span> {{ tr("legend.red") }}</div>
      <div><span class="dot gray"></span> {{ tr("legend.gray") }}</div>
    </div>

    <!-- Desktop / Tablet Matrix -->
    <div class="table-card desktop-matrix">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="indicator-col">{{ tr("indicatorColumn") }}</th>
              <th
                v-for="facility in localizedFacilities"
                :key="facility.survey_id"
              >
                {{ facility.facility_name }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="indicator in localizedIndicators" :key="indicator.key">
              <td class="indicator-col name">
                {{ indicator.name }}
                <div class="target">
                  {{ tr("target") }}: {{ indicator.target }}
                </div>
              </td>

              <td
                v-for="facility in indicator.facilities"
                :key="facility.survey_id"
                :class="getRagClass(facility.rag)"
                :title="`${indicator.name} : ${formatValue(facility.value)}`"
              >
                <span class="cell-value">
                  {{ formatValue(facility.value) }}
                </span>
              </td>
            </tr>

            <tr v-if="!localizedIndicators.length && !loading">
              <td colspan="20" class="empty">{{ tr("noData") }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Cards -->
    <div class="mobile-matrix">
      <article
        v-for="indicator in localizedIndicators"
        :key="`mobile-${indicator.key}`"
        class="indicator-card"
      >
        <div class="indicator-card-head">
          <div>
            <h3>{{ indicator.name }}</h3>
            <p>{{ tr("target") }}: {{ indicator.target }}</p>
          </div>

          <span class="indicator-code">{{
            indicator.code || indicator.key
          }}</span>
        </div>

        <div class="facility-values">
          <div
            v-for="facility in indicator.facilities"
            :key="`mobile-${indicator.key}-${facility.survey_id}`"
            class="facility-value-row"
          >
            <span class="facility-name">
              {{ getFacilityName(facility.survey_id) }}
            </span>

            <strong :class="getRagClass(facility.rag)">
              {{ formatValue(facility.value) }}
            </strong>
          </div>
        </div>
      </article>

      <div v-if="!localizedIndicators.length && !loading" class="mobile-empty">
        {{ tr("noData") }}
      </div>
    </div>

    <div class="note-box">
      {{ tr("note") }}
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const { t, locale } = useI18n();

const loading = ref(false);
const facilities = ref([]);
const indicators = ref([]);

const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`ragPage.${key}`, params);

const fetchRagMatrix = async () => {
  try {
    loading.value = true;

    const res = await api.get("/health-facilities/fis-rag-matrix");

    facilities.value = Array.isArray(res.data?.facilities)
      ? res.data.facilities
      : [];

    indicators.value = Array.isArray(res.data?.indicators)
      ? res.data.indicators
      : [];
  } catch (error) {
    facilities.value = [];
    indicators.value = [];
    console.error("RAG Matrix API Error:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchRagMatrix);

const localizedFacilities = computed(() =>
  facilities.value.map((facility) => ({
    ...facility,
    facility_name: isArabic.value
      ? facility.facility_name_ar ||
        facility.facility_name ||
        facility.name ||
        "-"
      : facility.facility_name_en ||
        facility.facility_name ||
        facility.facility_name_ar ||
        facility.name ||
        "-",
  }))
);

const localizedIndicators = computed(() =>
  indicators.value.map((indicator) => ({
    ...indicator,
    code: indicator.code || indicator.key,
    name: isArabic.value
      ? indicator.name_ar ||
        indicator.name ||
        indicator.title_ar ||
        indicator.title ||
        indicator.key
      : indicator.name_en ||
        indicator.title_en ||
        indicator.name ||
        indicator.title ||
        indicator.key,
    target:
      (isArabic.value ? indicator.target_ar : indicator.target_en) ||
      indicator.target ||
      "-",
  }))
);

const facilityNameMap = computed(() => {
  const map = new Map();

  localizedFacilities.value.forEach((facility) => {
    map.set(String(facility.survey_id), facility.facility_name);
  });

  return map;
});

const getFacilityName = (surveyId) =>
  facilityNameMap.value.get(String(surveyId)) || tr("unknownFacility");

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return tr("notAvailable");
  }

  if (!Number.isNaN(Number(value))) {
    return Number(value).toFixed(1).replace(".0", "");
  }

  return String(value);
}

function getRagClass(rag) {
  const value = String(rag || "").toLowerCase();

  if (value === "green") return "rag-green";
  if (value === "yellow" || value === "amber") return "rag-amber";
  if (value === "red") return "rag-red";
  if (value === "blue") return "rag-blue";

  return "rag-gray";
}
</script>

<style scoped>
.rag-page {
  min-height: 100vh;
  padding: 28px;
  overflow-x: hidden;
  background: #f4f6f9;
  color: #071126;
  font-family: "Inter", "Cairo", sans-serif;
}

.rag-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}

.rag-page[dir="ltr"] {
  font-family: "Inter", "Cairo", sans-serif;
}

.page-loading {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(244, 246, 249, 0.92);
  backdrop-filter: blur(6px);
}

.loader-card {
  width: 320px;
  max-width: calc(100% - 32px);
  padding: 34px 24px;
  text-align: center;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 22px;
  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;
  border: 6px solid #e2e8f0;
  border-top-color: #143a63;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  margin: 0 0 8px;
  color: #143a63;
  font-size: 18px;
  font-weight: 900;
}

.loader-card p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  margin-bottom: 18px;
}

.rag-page[dir="rtl"] .page-header {
  text-align: right;
}

.rag-page[dir="ltr"] .page-header {
  text-align: left;
}

.page-header h1 {
  margin: 0 0 8px;
  color: #061226;
  font-size: 26px;
  font-weight: 900;
  line-height: 1.5;
}

.page-header p {
  max-width: 1100px;
  margin: 0 0 8px;
  color: #52657d;
  font-size: 14px;
  line-height: 1.9;
}

.page-header span {
  display: inline-block;
  padding: 6px 14px;
  color: #64748b;
  font-size: 12px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
  color: #334155;
  font-size: 13px;
}

.legend > div {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 13px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
}

.dot {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 50%;
}

.green {
  background: #16a34a;
}

.amber {
  background: #d97706;
}

.red {
  background: #dc2626;
}

.gray {
  background: #64748b;
}

.table-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 35, 64, 0.05);
}

.table-wrap {
  max-height: 75vh;
  overflow: auto;
}

table {
  width: 100%;
  min-width: 1550px;
  border-collapse: collapse;
}

thead {
  background: #143a63;
}

th {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 14px 10px;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
  text-align: center;
  white-space: nowrap;
  background: #143a63;
  border-inline-end: 1px solid rgba(255, 255, 255, 0.12);
}

td {
  padding: 12px 10px;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
  border-inline-end: 1px solid #e2e8f0;
}

.indicator-col {
  position: sticky;
  z-index: 30;
  min-width: 280px;
  max-width: 280px;
  text-align: start;
}

.rag-page[dir="rtl"] .indicator-col {
  right: 0;
  box-shadow: -3px 0 8px rgba(0, 0, 0, 0.08);
}

.rag-page[dir="ltr"] .indicator-col {
  left: 0;
  box-shadow: 3px 0 8px rgba(0, 0, 0, 0.08);
}

thead .indicator-col {
  z-index: 40;
  background: #143a63;
}

tbody .indicator-col {
  background: #fff;
}

.name {
  color: #071126;
  font-weight: 900;
  line-height: 1.6;
}

.target {
  margin-top: 4px;
  color: #64748b;
  font-size: 11px;
}

.cell-value {
  display: inline-block;
  min-width: 52px;
}

.rag-green {
  color: #166534;
  background: #dcfce7;
}

.rag-amber {
  color: #92400e;
  background: #fef3c7;
}

.rag-red {
  color: #991b1b;
  background: #fee2e2;
}

.rag-blue {
  color: #1e40af;
  background: #dbeafe;
}

.rag-gray {
  color: #475569;
  background: #f1f5f9;
}

.empty {
  padding: 30px;
  color: #64748b;
  font-weight: 900;
  text-align: center;
}

.mobile-matrix {
  display: none;
}

.note-box {
  margin-top: 14px;
  padding: 13px 16px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.8;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
}

@media (max-width: 900px) {
  .rag-page {
    padding: 18px 12px 28px;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header p {
    font-size: 13px;
    line-height: 1.8;
  }

  .legend {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .legend > div {
    min-width: 0;
    padding: 9px 10px;
    font-size: 11px;
    border-radius: 12px;
  }

  .desktop-matrix {
    display: none;
  }

  .mobile-matrix {
    display: grid;
    gap: 14px;
  }

  .indicator-card {
    overflow: hidden;
    background: #fff;
    border: 1px solid #dbe3ee;
    border-inline-start: 5px solid #143a63;
    border-radius: 16px;
    box-shadow: 0 7px 20px rgba(15, 35, 64, 0.07);
  }

  .indicator-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  .indicator-card-head h3 {
    margin: 0 0 5px;
    color: #071126;
    font-size: 15px;
    font-weight: 900;
    line-height: 1.7;
  }

  .indicator-card-head p {
    margin: 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
  }

  .indicator-code {
    flex-shrink: 0;
    padding: 5px 9px;
    color: #143a63;
    font-size: 11px;
    font-weight: 900;
    direction: ltr;
    background: #eaf1f8;
    border: 1px solid #cbd9e8;
    border-radius: 8px;
  }

  .facility-values {
    display: grid;
    padding: 4px 14px;
  }

  .facility-value-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 11px 0;
    border-bottom: 1px dashed #e2e8f0;
  }

  .facility-value-row:last-child {
    border-bottom: 0;
  }

  .facility-name {
    min-width: 0;
    color: #475569;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.6;
    overflow-wrap: break-word;
  }

  .facility-value-row strong {
    min-width: 62px;
    flex-shrink: 0;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 900;
    text-align: center;
    border-radius: 9px;
  }

  .mobile-empty {
    padding: 28px;
    color: #64748b;
    font-weight: 900;
    text-align: center;
    background: #fff;
    border: 1px dashed #cbd5e1;
    border-radius: 14px;
  }

  .loader-card {
    width: calc(100% - 28px);
    padding: 28px 18px;
  }
}

@media (max-width: 420px) {
  .legend {
    grid-template-columns: 1fr;
  }

  .indicator-card-head {
    flex-direction: column;
  }

  .facility-value-row {
    align-items: flex-start;
  }

  .facility-name {
    font-size: 11px;
  }
}
</style>
